import { lazy, Suspense, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { Hub } from "aws-amplify/utils";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import theme from "./assets/theme";
import { ProtectedRoute, DevRoute } from "./routes";
import { Provider } from "react-redux";
import store from "./redux/store";
import "./styles.css";
import { Amplify } from "aws-amplify";
import { myAwsMobileConfig } from "./amplifysetup";
import LoadingScreen from "./pages/Loading";
import ChunkErrorBoundary from "./components/ChunkErrorBoundary";
import CookieConsent from "./components/CookieConsent";
import MaintenanceOverlay from "./components/MaintenanceOverlay";
import { installApiFailureWatch } from "./utils/maintenance";
// The home page is the main entry — keep it eager so a refresh of "/" paints
// immediately instead of flashing the Suspense loading screen.
import Presentation from "./pages/Home";
import { getCognitoAttributes } from "./redux/login";
import {
  resetAllUserState,
  resetUserScopedData,
} from "./Authentication/signoutWithDispatch";
import { useSelector } from "react-redux";
import logger from './utils/logger';
import { captureAttribution } from "./utils/attribution";

// Route-level code splitting: every other page loads on demand.
const Dashboard = lazy(() => import("./pages/Dashboard"));
const VerifyEmailPage = lazy(() => import("./pages/LandingPages/VerifyEmail"));
const PreferencesQuiz = lazy(() => import("./pages/LandingPages/Preferences/Index"));
const Return = lazy(() => import("./pages/Pricing/return"));
const MainLandingPages = lazy(() => import("./pages/LandingPages"));
const MobileApp = lazy(() => import("./pages/MobileApp"));
const SignInBasic = lazy(() => import("./pages/LandingPages/SignIn"));
const MonitoringPage = lazy(() => import("./pages/Monitoring"));
const QuestionPreview = lazy(() => import("./pages/Apply/questionPreview"));
const FilterBarPreview = lazy(() => import("./pages/Apply/filterBarPreview"));
const DeckStatePreview = lazy(() => import("./pages/Apply/deckStatePreview"));
const AnswerReviewPreview = lazy(() => import("./pages/Apply/answerReviewPreview"));
const ProfilePreview = lazy(() => import("./pages/Dashboard/Profile/profilePreview"));
const IntakePreview = lazy(() => import("./pages/Dashboard/Profile/intakePreview"));
const EmailPreview = lazy(() => import("./pages/LandingPages/Preferences/emailPreview"));
const EarnPreview = lazy(() => import("./pages/Dashboard/Earn/earnPreview"));
const PremiumPreview = lazy(() => import("./pages/Pricing/premiumPreview"));
const MailboxPreview = lazy(() => import("./pages/Dashboard/Mailbox/mailboxPreview"));
const NextStepsPreview = lazy(() => import("./pages/Dashboard/MyJobs/nextStepsPreview"));
const LinksPage = lazy(() => import("./pages/Links"));
const CreatorLanding = lazy(() => import("./pages/CreatorLanding"));


logger.log("AWS Amplify Configuration:", myAwsMobileConfig);

Amplify.configure(myAwsMobileConfig);

// A failed API call (no response, or a 5xx) prompts a maintenance-switch check, so
// people mid-swipe learn about an outage without waiting for the 2-minute check.
installApiFailureWatch();

// Records first-touch UTM/referrer attribution on every route, not just Home,
// so UTM'd links landing on /mobile-app, /signup, etc. aren't lost.
function AttributionCapture() {
  const location = useLocation();

  useEffect(() => {
    captureAttribution(location.search, document.referrer, location.pathname);
  }, [location.search, location.pathname]);

  return null;
}

function App() {
  const dispatch = useDispatch();
  const { signedIn, isLoading, userId } = useSelector((state) => state.loginUser);

  // Belt-and-braces against cross-account data bleed: if the signed-in identity
  // changes without a sign-out reaching us first, drop everything the previous
  // identity loaded. Only the data slices are cleared — resetting loginUser here
  // would wipe the very userId this effect keys on.
  const lastUserIdRef = useRef(null);
  useEffect(() => {
    if (!userId) return;
    if (lastUserIdRef.current && lastUserIdRef.current !== userId) {
      logger.log("Signed-in user changed — clearing previous account's data.");
      resetUserScopedData(dispatch);
    }
    lastUserIdRef.current = userId;
  }, [userId, dispatch]);

  useEffect(() => {
    // Initial fetch of user attributes — once on mount
    dispatch(getCognitoAttributes());
  }, [dispatch]);

  useEffect(() => {
    // Listen to auth events
    const hubListener = async ({ payload }) => {
      switch (payload.event) {
        case "signedIn":
          logger.log("User has signed in successfully.");
          if (!signedIn && !isLoading) dispatch(getCognitoAttributes());
          break;
        case "signedOut":
          logger.log("User has signed out successfully.");
          // Full wipe, not just the login slice: sign-outs that bypass the
          // logout button (session expiry, another tab, Cognito) would
          // otherwise hand the next account this one's cached mail/jobs.
          resetAllUserState(dispatch);
          break;
        case "tokenRefresh":
          logger.log("Auth tokens have been refreshed.");
          break;
        case "tokenRefresh_failure":
          logger.log("Failure while refreshing auth tokens.");
          resetAllUserState(dispatch); // Ensure state reflects signed-out status
          break;
        default:
          break;
      }
    };

    // Hub.listen returns an unsubscribe function in Amplify v6.
    // signedIn/isLoading are in the deps so the listener never closes over
    // stale auth state (re-subscribing is cheap).
    const unsubscribe = Hub.listen("auth", hubListener);
    return unsubscribe;
  }, [dispatch, signedIn, isLoading]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AttributionCapture />
      <CookieConsent />
      <MaintenanceOverlay />
      <ChunkErrorBoundary>
      <Suspense fallback={<LoadingScreen />}>
      <Routes>
        <Route path="/" element={<Presentation />} />
        <Route path="/preferences-quiz" element={
          <ProtectedRoute>
            <PreferencesQuiz />
          </ProtectedRoute>
        } />
        <Route path="/loading" element={<LoadingScreen />} />
        <Route path="/landing-pages/*" element={<MainLandingPages />} />
        {/* Short legal URLs. Shipped iOS builds link to /privacy and /terms
            from the sign-up screen and Settings, and those installs can't be
            updated retroactively — without these the catch-all below sent
            every one of those taps to the home page, so the App Review
            legal links were dead. Keep these forever. */}
        <Route path="/privacy" element={<Navigate to="/landing-pages/privacy" replace />} />
        <Route path="/terms" element={<Navigate to="/landing-pages/terms" replace />} />
        {/* Mobile app waitlist promo (cinematic, standalone like Home) */}
        <Route path="/mobile-app" element={<MobileApp />} />
        <Route path="/landing-pages/mobile-app" element={<Navigate to="/mobile-app" replace />} />
        {/* Link-in-bio landing page for social profiles */}
        <Route path="/links" element={<LinksPage />} />
        <Route path="/c/:code" element={<CreatorLanding />} />
        <Route
          path="/dashboard/*"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route path="/signin" element={<SignInBasic />} />
        <Route path="/signup" element={<SignInBasic />} />
        <Route path="/signin/emailverification" element={<VerifyEmailPage />} />
        <Route path="/return" element={
          <ProtectedRoute>
            <Return />
          </ProtectedRoute>
        } />
        {/* Dev only routes */}
        <Route path="/monitoring" element={
          <DevRoute>
            <MonitoringPage />
          </DevRoute>
        } />
        {/* Visual harness for the apply-time question popup — ?case=yesno|mixed|longselect|file */}
        <Route path="/question-preview" element={
          <DevRoute>
            <QuestionPreview />
          </DevRoute>
        } />
        {/* Visual harness for the deck's filter bar — ?plan=premium|free */}
        <Route path="/filter-bar-preview" element={
          <DevRoute>
            <FilterBarPreview />
          </DevRoute>
        } />
        {/* Visual harness for the deck's empty states — ?case=unavailable|profileIncomplete|exhausted */}
        <Route path="/deck-state-preview" element={
          <DevRoute>
            <DeckStatePreview />
          </DevRoute>
        } />
        
        {/* Visual harness for the answer review loop — ?case=queue|memory|archive|deck|resumePreview|prep|reply
            (resumePreview takes &state=tailored|notTailored|premiumGated|off;
             prep takes &state=full|thankYou|packFor|message|preparing|unverified|none|flagOff|error;
             reply takes &state=draft|acknowledgement|repliedBefore|notDrafted|cap|inProgress|
             outcomeUnknown|ambiguous|replayed|flagOff|unverified|noReply) */}
        <Route path="/answer-review-preview" element={
          <DevRoute>
            <AnswerReviewPreview />
          </DevRoute>
        } />
        {/* Visual harness for the Profile page's personalization tabs —
            ?tab=questions|anecdotes|personalization[&case=empty] */}
        <Route path="/profile-preview" element={
          <DevRoute>
            <ProfilePreview />
          </DevRoute>
        } />
        {/* Visual harness for the intake surfaces (NEXT_STAGE_INTAKE) — ?case=mixed|perfect|off */}
        <Route path="/intake-preview" element={
          <DevRoute>
            <IntakePreview />
          </DevRoute>
        } />
        {/* Visual harness for the Premium page and Settings plan card:
            ?plan=<membershipPlan>&view=pricing|settings&apple=1&stale=1&cancelled=1&plansDown=1 */}
        <Route path="/premium-preview" element={
          <DevRoute>
            <PremiumPreview />
          </DevRoute>
        } />
        {/* Visual harness for Refer & Earn — ?case=mixed|empty|capped */}
        <Route path="/earn-preview" element={
          <DevRoute>
            <EarnPreview />
          </DevRoute>
        } />
        {/* Visual harness for the quiz's email step, including the state the user lands
            on after a provider consent redirect — ?case=fresh|connected|complete|forwarded */}
        <Route path="/email-preview" element={
          <DevRoute>
            <EmailPreview />
          </DevRoute>
        } />
        {/* Visual harness for the mailbox's job-mail / promotions split — ?view=job|promotions */}
        <Route path="/mailbox-preview" element={
          <DevRoute>
            <MailboxPreview />
          </DevRoute>
        } />
        {/* Visual harness for "what to do next" on My Jobs, the deck card and in the Mailbox —
            ?view=board|dialog|mailbox|email|deck
            (&email=phone|assessment|confirmation|background|survey|rejection)
            (&tab=Archived|Full Description|Description) (&loading=1) */}
        <Route path="/next-steps-preview" element={
          <DevRoute>
            <NextStepsPreview />
          </DevRoute>
        } />
        {/* Static files routes to prevent redirect */}
        <Route path="/sitemap.xml" element={null} />
        <Route path="/robots.txt" element={null} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
      </ChunkErrorBoundary>
    </ThemeProvider>
  );
}

export default function WrappedApp() {
  return (
    <Provider store={store}>
      <App />
    </Provider>
  );
}